import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
// Icons
import { IoMdMenu } from 'react-icons/io';
import { FaChevronDown } from 'react-icons/fa6';
// Context
import { useUser } from '../../context/UserContext';
// Nav data
import { getNavLinkItemsArray } from '../../utils/data/NavData';

function Navbar() {
  const { user } = useUser();
  const navLinkItemsArray = getNavLinkItemsArray(user);

  const [isPhoneNavOpen, setIsPhoneNavOpen] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState({});

  const togglePhoneNav = () => setIsPhoneNavOpen((prev) => !prev);

  return (
    <nav
      role='navigation'
      aria-label='Main Navigation'
      className='fixed shadow-md h-fit w-full'
    >
      <div className='relative bg-colour2'>
        <section className='grid grid-flow-col px-4 py-4'>
          <section className='grid items-center lg:hidden text-colour1 text-xl'>
            MS MARIA
          </section>
          <section className='grid justify-end items-center lg:justify-center'>
            {/* Mobile screen */}
            <button
              type='button'
              aria-label={
                isPhoneNavOpen
                  ? 'Close navigation menu'
                  : 'Open navigation menu'
              }
              aria-expanded={isPhoneNavOpen}
              aria-controls='mobile-nav-panel'
              onClick={togglePhoneNav}
              className='grid lg:hidden w-fit h-fit items-center justify-center text-4xl text-white cursor-pointer'
              title={isPhoneNavOpen ? 'Close menu' : 'Open menu'}
            >
              <IoMdMenu
                className='active:scale-90 duration-300'
                aria-hidden='true'
              />
            </button>

            {/* Large screen */}
            <ul className='hidden lg:flex gap-4 lg:gap-7 w-fit mx-auto items-center'>
              {navLinkItemsArray.map(({ path, label, subpages }) =>
                subpages ? (
                  <li key={label} className='relative group'>
                    {/* Use a button so keyboard users can focus the trigger */}
                    <button
                      type='button'
                      className='text-xl md:text-lg lg:text-3xl no__highlights text-colour1 font-semibold font-titleFont cursor-pointer hover:brightness-75 duration-200 active:scale-75'
                      aria-haspopup='true'
                      aria-expanded='false'
                      aria-controls={`menu-${label}`}
                      // no JS state on desktop: menu is revealed via :hover and :focus-within
                    >
                      {label}
                    </button>

                    <ul
                      id={`menu-${label}`}
                      className='absolute left-0 top-full mt-4 bg-colour2 top_shadow border-2 border-solid border-colour4 shadow-cardShadowBold p-3 grid gap-2 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-200 min-w-[220px]'
                      role='menu'
                      aria-label={`${label} submenu`}
                    >
                      {subpages.map(({ path: subPath, label: subLabel }) => (
                        <li
                          key={subLabel}
                          className='border-b-[1px] border-solid border-colour1 last:border-b-0'
                          role='none'
                        >
                          <NavItem
                            url={subPath}
                            title={subLabel}
                            className='!text-colour3 block px-2 py-2'
                            role='menuitem'
                          />
                        </li>
                      ))}
                    </ul>
                  </li>
                ) : (
                  <li key={label}>
                    <NavItem
                      url={path}
                      title={label}
                      className='block px-1 py-2'
                    />
                  </li>
                )
              )}
            </ul>
          </section>
        </section>

        {/* Phone navbar */}
        {isPhoneNavOpen && (
          <section
            id='mobile-nav-panel'
            className='absolute top-full left-0 w-full bg-colour2 border-b-2 border-t-2 border-solid border-colour2 transition-transform duration-300'
          >
            <ul className='grid gap-4 text-center py-6 shadow-md shadow-colour2/50 no__highlights'>
              {navLinkItemsArray.map(({ path, label, subpages }) =>
                subpages ? (
                  <li key={label}>
                    <div className='grid justify-center'>
                      <button
                        type='button'
                        className='flex justify-center gap-3 hover:brightness-75 duration-200 cursor-pointer items-center'
                        aria-expanded={!!openDropdowns[label]}
                        aria-controls={`mobile-menu-${label}`}
                        onClick={() =>
                          setOpenDropdowns((prev) => ({
                            ...prev,
                            [label]: !prev[label],
                          }))
                        }
                        title={label}
                      >
                        <span className='text-xl md:text-lg lg:text-3xl text-colour1 font-medium font-titleFont no__highlights'>
                          {label}
                        </span>
                        <span
                          className={`flex transition-transform duration-300 text-colour1 ${
                            openDropdowns[label] ? 'rotate-180 mb-1.5' : 'mt-1'
                          }`}
                          aria-hidden='true'
                        >
                          <FaChevronDown />
                        </span>
                      </button>
                    </div>

                    {openDropdowns[label] && (
                      <ul
                        id={`mobile-menu-${label}`}
                        className='grid gap-3 mt-2 bg-colour2 full_shadow_black_invert py-4 border-b-2 border-t-2 border-solid border-colour1 no__highlights'
                        role='menu'
                        aria-label={`${label} submenu`}
                      >
                        {subpages.map(({ path: subPath, label: subLabel }) => (
                          <li key={subLabel} role='none'>
                            <NavItem
                              url={subPath}
                              title={subLabel}
                              className='block px-2 py-2'
                              role='menuitem'
                            />
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ) : (
                  <li key={label}>
                    <NavItem
                      url={path}
                      title={label}
                      className='block px-2 py-2'
                    />
                  </li>
                )
              )}
            </ul>
          </section>
        )}
      </div>
    </nav>
  );
}

const NavItem = ({ url, title, className = '', role }) => {
  return (
    <NavLink
      to={url}
      className={`text-xl md:text-lg lg:text-3xl text-colour1 font-semibold font-titleFont hover:brightness-75 duration-200 ${className}`}
      aria-label={`${title} page navigation tab`}
      aria-current={({ isActive }) => (isActive ? 'page' : undefined)}
      title={title}
      role={role}
      style={({ isActive }) => (isActive ? { color: '#800020' } : undefined)}
    >
      {title}
    </NavLink>
  );
};

export default Navbar;
